import type { MarketPoolQuoteSnapshot } from '@morfi/market-math';
import type { MarketPlatformConfig, MarketPoolRow } from './market-pool-projection.js';

export type MarketPoolSnapshotEntry =
  | { readonly status: 'available'; readonly snapshot: MarketPoolQuoteSnapshot }
  | { readonly status: 'pending' }
  | { readonly status: 'missing' }
  | { readonly status: 'stale' }
  | { readonly status: 'error' };

export interface MarketPoolSnapshotEntries {
  readonly byMarket: ReadonlyMap<string, MarketPoolSnapshotEntry>;
  readonly dataUpdatedAtByMarket: ReadonlyMap<string, number>;
  readonly failedMarkets: ReadonlySet<string>;
}

export function marketPoolSnapshotEntries(input: {
  readonly markets: readonly string[];
  readonly rows: readonly MarketPoolRow[];
  readonly snapshots: Readonly<Record<string, MarketPoolQuoteSnapshot>> | undefined;
  readonly configs: Readonly<Record<string, MarketPlatformConfig>> | undefined;
  readonly marketError: boolean;
  readonly marketPending: boolean;
  readonly marketStale: boolean;
  readonly configPending: boolean;
  readonly marketUpdatedAt: number;
  readonly configUpdatedAt: number;
}): MarketPoolSnapshotEntries {
  const byMarket = new Map<string, MarketPoolSnapshotEntry>();
  const dataUpdatedAtByMarket = new Map<string, number>();
  const failedMarkets = new Set<string>();
  const rows = new Map(input.rows.map((row) => [row.pubkey, row]));
  for (const market of input.markets) {
    const row = rows.get(market);
    const snapshot = input.snapshots?.[market];
    let entry: MarketPoolSnapshotEntry;
    if (input.marketError) entry = { status: 'error' };
    else if (input.marketPending) entry = { status: 'pending' };
    else if (row === undefined) {
      entry = { status: 'missing' };
      dataUpdatedAtByMarket.set(market, input.marketUpdatedAt);
    } else if (input.marketStale) entry = { status: 'stale' };
    else if (row.status === 'trading' && input.configs?.[row.marketConfig] === undefined) {
      entry = { status: input.configPending ? 'pending' : 'error' };
    } else if (snapshot === undefined) entry = { status: 'error' };
    else {
      entry = { status: 'available', snapshot };
      dataUpdatedAtByMarket.set(market, Math.max(input.marketUpdatedAt, input.configUpdatedAt));
    }
    byMarket.set(market, entry);
    if (entry.status === 'error') failedMarkets.add(market);
  }
  return { byMarket, dataUpdatedAtByMarket, failedMarkets };
}
